import moment from 'moment';
import type {
  CreateAnchorBackendPayload,
  InsightBackendPayload,
  UpdateAnchorBackendPayload,
  UpdateAnomalyBackendPayload,
} from '../../../Insights/types';
import type { AnomalyBandWithForm } from './AnomalyBand/types';
import { enMessages } from '../../../../../../../messages/en-US';

export type OptionItem = {
  text: string;
  value: string;
};

export const STREAM_OPTIONS: OptionItem[] = [
  { text: 'Flow', value: 'volumeDispensed' },
  { text: 'Pump', value: 'pump' },
  { text: 'Pressure', value: 'pressure' },
  { text: 'Temperature', value: 'temperature' },
  { text: 'RSSI', value: 'rssi' },
  { text: 'Voltage', value: 'voltage' },
];

export const CRITICALITY_OPTIONS: OptionItem[] = [
  { text: 'Low', value: 'Low' },
  { text: 'Medium', value: 'Medium' },
  { text: 'High', value: 'High' },
  { text: 'Critical', value: 'Critical' },
];

type LocalizationServiceLike = {
  toLanguageString: (key: string, defaultValue: string) => string;
};

export const getLocalizedStreamOptions = (localizationService: LocalizationServiceLike): OptionItem[] => [
  { text: localizationService.toLanguageString('custom.flow', enMessages.custom.flow), value: 'volumeDispensed' },
  { text: localizationService.toLanguageString('custom.pump', enMessages.custom.pump), value: 'pump' },
  { text: localizationService.toLanguageString('custom.pressure', enMessages.custom.pressure), value: 'pressure' },
  {
    text: localizationService.toLanguageString('custom.temperature', enMessages.custom.temperature),
    value: 'temperature',
  },
  { text: localizationService.toLanguageString('custom.rssi', enMessages.custom.rssi), value: 'rssi' },
  { text: localizationService.toLanguageString('custom.voltageTooltip', 'Voltage'), value: 'voltage' },
];

export const getLocalizedCriticalityOptions = (localizationService: LocalizationServiceLike): OptionItem[] => [
  { text: localizationService.toLanguageString('custom.low', enMessages.custom.low), value: 'Low' },
  { text: localizationService.toLanguageString('custom.medium', enMessages.custom.medium), value: 'Medium' },
  { text: localizationService.toLanguageString('custom.high', enMessages.custom.high), value: 'High' },
  { text: localizationService.toLanguageString('custom.critical', enMessages.custom.critical), value: 'Critical' },
];

const CRITICALITY_ALIASES: Record<string, string> = {
  Minor: 'Low',
  Moderate: 'Medium',
  Major: 'High',
  Low: 'Low',
  Medium: 'Medium',
  High: 'High',
  Critical: 'Critical',
};

export const normalizeCriticality = (value: string | undefined): string | undefined =>
  value ? CRITICALITY_ALIASES[value] ?? value : undefined;

const STREAM_VALUE_BY_TEXT = new Map<string, string>(
  STREAM_OPTIONS.map((opt) => [opt.text.toLowerCase(), opt.value])
);
const STREAM_VALID_VALUES = new Set(STREAM_OPTIONS.map((opt) => opt.value));

export const normalizeStreamValue = (value: string): string => {
  if (!value) return value;
  if (STREAM_VALID_VALUES.has(value)) return value;
  return STREAM_VALUE_BY_TEXT.get(value.toLowerCase()) ?? value;
};

export const normalizeStreamValues = (values: string[] | undefined): string[] =>
  Array.isArray(values) ? values.map(normalizeStreamValue) : [];

export const ML_AUTHOR_NAME = 'greaseboss ml';

export const isMlAuthor = (user: string | undefined | null): boolean =>
  !!user && user.trim().toLowerCase() === ML_AUTHOR_NAME;

export const isMlAnomaly = (anomalyId: string | undefined | null): boolean =>
  !!anomalyId && anomalyId.trim().length > 0;

export const CONFIDENCE_OPTIONS = Array.from({ length: 10 }, (_, i) => ({
  text: String(i + 1),
  value: i + 1,
}));

export const FIELD_SPACING = '0.05rem';

export const streamsRequiredValidator = (value: unknown): string | undefined =>
  Array.isArray(value) && value.length > 0 ? undefined : enMessages.custom.fieldIsRequired;

export const criticalityRequiredValidator = (value: unknown): string | undefined =>
  value != null && value !== '' ? undefined : enMessages.custom.fieldIsRequired;

const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !Number.isNaN(value.getTime());

// Anomalies describe what already happened — no part of the period may be in the future.
export const isFutureDate = (value: Date): boolean => value.getTime() > Date.now();

export const startDateTimeValidator = (
  value: unknown,
  valueGetter: (name: string) => unknown
): string | undefined => {
  if (!isValidDate(value)) {
    return enMessages.custom.fieldIsRequired;
  }

  const endDateTime = valueGetter('endDateTime');
  if (isValidDate(endDateTime) && value.getTime() > endDateTime.getTime()) {
    return enMessages.custom.startMustBeBeforeEnd;
  }

  return undefined;
};

export const endDateTimeValidator = (
  value: unknown,
  valueGetter: (name: string) => unknown
): string | undefined => {
  if (!isValidDate(value)) {
    return enMessages.custom.fieldIsRequired;
  }

  const startDateTime = valueGetter('startDateTime');
  if (isValidDate(startDateTime) && value.getTime() < startDateTime.getTime()) {
    return enMessages.custom.endMustBeAfterStart;
  }

  return undefined;
};

export const toUnixSeconds = (value: Date): number => Math.floor(value.getTime() / 1000);

/**
 * An anomaly is "ongoing" (open-ended) when it has no end date. The backend receives
 * `null` as `endDate`; on read, a missing/null/unparseable end date — or the legacy
 * year-9999 sentinel — means ongoing.
 */
export const isOngoingEndDate = (value: string | number | Date | null | undefined): boolean => {
  if (value == null || value === '') return true;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return true;
  return date.getUTCFullYear() >= 9999;
};

export const toComboValue = (v: unknown): string | undefined => {
  if (v == null) return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && v !== null && 'value' in v) return (v as { value?: string }).value;
  if (typeof v === 'object' && v !== null && 'text' in v) return (v as { text?: string }).text;
  return undefined;
};

export const toMultiValues = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => {
      if (typeof item === 'string') {
        return item;
      }

      if (item && typeof item === 'object') {
        if ('value' in item && typeof (item as { value?: unknown }).value === 'string') {
          return (item as { value: string }).value;
        }

        if ('text' in item && typeof (item as { text?: unknown }).text === 'string') {
          return (item as { text: string }).text;
        }
      }

      return '';
    })
    .filter((item): item is string => item.trim().length > 0);
};

export const toOptions = (values: string[]): OptionItem[] =>
  values.map((value) => ({ text: value, value }));

const toOption = (value: string): OptionItem => ({ text: value, value });

export const mergeOptions = (
  current: OptionItem[],
  incoming: OptionItem[]
): OptionItem[] => {
  const merged = [...current];

  incoming.forEach((item) => {
    if (!merged.some((existing) => existing.value === item.value)) {
      merged.push(item);
    }
  });

  return merged;
};

export const ensureOptions = (
  current: OptionItem[],
  values: string[] | undefined
): OptionItem[] => {
  if (!Array.isArray(values) || values.length === 0) {
    return current;
  }

  return values.reduce<OptionItem[]>((acc, value) => {
    if (!value || acc.some((item) => item.value === value)) {
      return acc;
    }

    return [...acc, toOption(value)];
  }, current);
};

export const formatNoteDate = (timestamp: string): string => {
  const date = moment(timestamp);
  return date.isValid() ? date.format('DD MMM YYYY HH:mm') : timestamp;
};

const READ_ONLY_FALLBACK = '-';

export const toReadOnlyValue = (value: string | number | undefined | null): string =>
  value == null || value === '' ? READ_ONLY_FALLBACK : String(value);

interface GetInitialValuesArgs {
  selectedBand: AnomalyBandWithForm | undefined;
  criticalityOptions: OptionItem[];
  indicatorOptions: OptionItem[];
  rootCauseOptions: OptionItem[];
  actionOptions: OptionItem[];
}

export const getInitialValuesFromBand = ({
  selectedBand,
  criticalityOptions,
  indicatorOptions,
  rootCauseOptions,
  actionOptions,
}: GetInitialValuesArgs) => {
  if (!selectedBand) {
    return {
      startDateTime: undefined,
      endDateTime: new Date(),
      streams: [],
      criticality: undefined,
      indicators: [],
      rootCauses: [],
      actions: [],
      confidence: undefined,
      savings: undefined,
      userNotes: '',
      ongoing: true,
    };
  }

  const streams = selectedBand.streams ?? [];
  const streamItems =
    streams.length > 0
      ? streams.map(
          (value) => STREAM_OPTIONS.find((option) => option.value === value) ?? { text: value, value }
        )
      : [];

  const ongoing = selectedBand.ongoing ?? true;

  return {
    startDateTime: new Date(selectedBand.startTime * 1000),
    // Ongoing anomalies have no end date — default the field to the current date/time.
    endDateTime: ongoing ? new Date() : new Date(selectedBand.endTime * 1000),
    streams: streamItems,
    criticality: normalizeCriticality(selectedBand.criticality)
      ? criticalityOptions.find((option) => option.value === normalizeCriticality(selectedBand.criticality)) ??
        toOption(normalizeCriticality(selectedBand.criticality)!)
      : undefined,
    indicators: (selectedBand.indicators ?? []).map(
      (value) => indicatorOptions.find((option) => option.value === value) ?? toOption(value)
    ),
    rootCauses: (selectedBand.rootCauses ?? []).map(
      (value) => rootCauseOptions.find((option) => option.value === value) ?? toOption(value)
    ),
    actions: (selectedBand.actions ?? []).map(
      (value) => actionOptions.find((option) => option.value === value) ?? toOption(value)
    ),
    confidence: CONFIDENCE_OPTIONS.find((option) => option.value === selectedBand.confidence) ?? undefined,
    savings: selectedBand.savings ?? undefined,
    userNotes: selectedBand.userNotes ?? '',
    ongoing,
  };
};

interface BuildAnomalyPayloadArgs {
  band: AnomalyBandWithForm;
  assetId: string;
  endpointId: string;
  userName?: string;
  userId?: string;
}

export const buildAnomalyPayload = ({
  band,
  assetId,
  endpointId,
  userName,
  userId,
}: BuildAnomalyPayloadArgs): InsightBackendPayload => {
  const authorName = band.createdByUserName ?? userName ?? '';
  const authorId = band.createdByUserId ?? userId ?? '';
  const noteComment = band.userNotes?.trim() ?? '';
  const existingNotes = band.notes ?? [];
  const pendingNotes = band.pendingNotes ?? [];
  const notes =
    noteComment.length > 0
      ? [
          ...existingNotes,
          ...pendingNotes,
          {
            timestamp: new Date().toISOString(),
            user: userName ?? '',
            comment: noteComment,
          },
        ]
      : [...existingNotes, ...pendingNotes];

  return {
    machineId: assetId,
    pointId: endpointId,
    user: authorName,
    userId: authorId,
    startDate: new Date(band.startTime * 1000).toISOString(),
    endDate: band.ongoing ? null : new Date(band.endTime * 1000).toISOString(),
    anomalyId: band.anomalyId ?? '',
    streams: band.streams ?? [],
    form: {
      criticality: normalizeCriticality(band.criticality) ?? '',
      indicators: band.indicators ?? [],
      rootCauses: band.rootCauses ?? [],
      actions: band.actions ?? [],
      confidence: band.confidence ?? 0,
      estimatedSavings: band.savings ?? 0,
    },
    notes,
    revisionHistory: [],
    mlResults: band.mlResults ?? [],
  };
};

export const buildUpdateAnomalyPayload = ({
  band,
}: BuildAnomalyPayloadArgs): UpdateAnomalyBackendPayload => ({
  startDate: new Date(band.startTime * 1000).toISOString(),
  endDate: band.ongoing ? null : new Date(band.endTime * 1000).toISOString(),
  streams: band.streams ?? [],
  form: {
    criticality: normalizeCriticality(band.criticality) ?? '',
    indicators: band.indicators ?? [],
    rootCauses: band.rootCauses ?? [],
    actions: band.actions ?? [],
    confidence: band.confidence ?? 0,
    estimatedSavings: band.savings ?? 0,
  },
});

const buildMergedNotes = (band: AnomalyBandWithForm, userName?: string) => {
  const noteComment = band.userNotes?.trim() ?? '';
  const existingNotes = band.notes ?? [];
  const pendingNotes = band.pendingNotes ?? [];

  return noteComment.length > 0
    ? [
        ...existingNotes,
        ...pendingNotes,
        {
          timestamp: new Date().toISOString(),
          user: userName ?? '',
          comment: noteComment,
        },
      ]
    : [...existingNotes, ...pendingNotes];
};

export const buildCreateAnchorPayload = ({
  band,
  assetId,
  endpointId,
  userName,
  userId,
}: BuildAnomalyPayloadArgs): CreateAnchorBackendPayload => {
  const authorName = band.createdByUserName ?? userName ?? '';
  const authorId = band.createdByUserId ?? userId ?? '';
  const notes = buildMergedNotes(band, userName);

  return {
    machineId: assetId,
    pointId: endpointId,
    user: authorName,
    userId: authorId,
    startDate: new Date(band.startTime * 1000).toISOString(),
    endDate: new Date(band.endTime * 1000).toISOString(),
    streams: band.streams ?? [],
    notes,
    revisionHistory: band.revisionHistory ?? [],
  };
};

export const buildUpdateAnchorPayload = ({
  band,
  endpointId: _endpointId,
  assetId: _assetId,
  userName: _userName,
  userId: _userId,
}: BuildAnomalyPayloadArgs): UpdateAnchorBackendPayload => {
  return {
    startDate: new Date(band.startTime * 1000).toISOString(),
    endDate: new Date(band.endTime * 1000).toISOString(),
    streams: band.streams ?? [],
  };
};
